/**
 * テキスト入力プロパティ カスタムエレメント
 *
 * 商品の数量に連動してテキスト入力フィールドを動的に増減する。
 * 入力値は Shopify のラインアイテムプロパティ（properties[ラベル N]）として
 * カート・注文情報に保存される。
 *
 * form 属性で商品フォームに紐づけているため、
 * <product-form> 外に配置しても FormData で自動収集される。
 */
if (!customElements.get('text-input-property')) {
  customElements.define(
    'text-input-property',
    class TextInputProperty extends HTMLElement {
      constructor() {
        super();
        this.fieldsContainer = this.querySelector('.text-input-property__fields');
        this.sectionId = this.dataset.sectionId;
        this.blockId = this.dataset.blockId;
        this.propertyLabel = this.dataset.label;
        this.placeholder = this.dataset.placeholder;
        this.maxLength = this.dataset.maxLength;
        this.productFormId = this.dataset.formId;
        this.currentQuantity = 1;
      }

      connectedCallback() {
        // 同セクション内の数量inputを取得し、現在の数量でフィールドを初期描画
        this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);

        if (this.quantityInput) {
          this.currentQuantity = parseInt(this.quantityInput.value) || 1;
          this.renderFields(this.currentQuantity);

          // 数量inputの直接変更を監視
          this._boundOnQuantityChange = this.onQuantityChange.bind(this);
          this.quantityInput.addEventListener('change', this._boundOnQuantityChange);
        }

        // +/- ボタンによる数量変更を Pub/Sub 経由で監視
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.quantityUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.quantityUpdate,
            this.onQuantityUpdate.bind(this)
          );
        }
      }

      // DOM から切り離された際にイベントリスナーを解除
      disconnectedCallback() {
        if (this.quantityInput && this._boundOnQuantityChange) {
          this.quantityInput.removeEventListener('change', this._boundOnQuantityChange);
        }
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
      }

      // 数量inputの change イベントハンドラ
      onQuantityChange(event) {
        const newQuantity = parseInt(event.target.value) || 1;
        if (newQuantity !== this.currentQuantity) {
          this.currentQuantity = newQuantity;
          this.renderFields(newQuantity);
        }
      }

      // Pub/Sub 経由の数量更新ハンドラ（+/- ボタン操作時）
      onQuantityUpdate() {
        if (this.quantityInput) {
          const newQuantity = parseInt(this.quantityInput.value) || 1;
          if (newQuantity !== this.currentQuantity) {
            this.currentQuantity = newQuantity;
            this.renderFields(newQuantity);
          }
        }
      }

      /**
       * 指定された数量分のフィールドを再描画する。
       * 既存の入力値は保持し、数量が減った場合のみ末尾のフィールドが消える。
       */
      renderFields(quantity) {
        // 再描画前に既存の入力値を退避
        const preservedValues = {};
        this.fieldsContainer.querySelectorAll('input[type="text"]').forEach((input, fieldIndex) => {
          preservedValues[fieldIndex + 1] = input.value;
        });

        this.fieldsContainer.innerHTML = '';

        for (let fieldNumber = 1; fieldNumber <= quantity; fieldNumber++) {
          const fieldGroupEl = this.createFieldGroup(fieldNumber, preservedValues[fieldNumber] || '');
          this.fieldsContainer.appendChild(fieldGroupEl);
        }
      }

      /**
       * 1つ分のフィールドグループ（input + label + エラーメッセージ）を生成する。
       * name属性は properties[ラベル N] 形式で、ラインアイテムプロパティとして送信される。
       */
      createFieldGroup(index, preservedValue) {
        const fieldGroupEl = document.createElement('div');
        fieldGroupEl.className = 'text-input-property__field-group';
        fieldGroupEl.dataset.index = index;

        const fieldWrapper = document.createElement('div');
        fieldWrapper.className = 'field';

        const textInput = document.createElement('input');
        textInput.className = 'field__input';
        textInput.type = 'text';
        textInput.name = `properties[${this.propertyLabel} ${index}]`;
        textInput.id = `TextInput-${this.blockId}-${this.sectionId}-${index}`;
        textInput.placeholder = this.placeholder;
        textInput.maxLength = this.maxLength;
        textInput.required = true;
        textInput.value = preservedValue;
        textInput.setAttribute('form', this.productFormId);

        const inputLabel = document.createElement('label');
        inputLabel.className = 'field__label';
        inputLabel.setAttribute('for', textInput.id);
        inputLabel.textContent = `${this.propertyLabel} ${index}`;

        const errorMessageEl = document.createElement('span');
        errorMessageEl.className = 'text-input-property__error-message';
        errorMessageEl.hidden = true;
        errorMessageEl.textContent = `${this.propertyLabel}を入力してください`;

        // 入力時にエラー状態をリアルタイムで解除
        textInput.addEventListener('input', () => {
          if (textInput.value.trim()) {
            textInput.classList.remove('field__input--error');
            errorMessageEl.hidden = true;
          }
        });

        fieldWrapper.appendChild(textInput);
        fieldWrapper.appendChild(inputLabel);
        fieldGroupEl.appendChild(fieldWrapper);
        fieldGroupEl.appendChild(errorMessageEl);

        return fieldGroupEl;
      }
    }
  );
}
